
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from "react";

import {
    createSimliClient,
    stopSimliClient
} from "../services/EvaSimliService";

import {
    generateSpeech
} from "../services/EvaSpeechService";


//========================================================
// Eva Video
//
// Displays and manages the Simli avatar rendering layer.
//
// Simli provides the visual avatar only.
// Eva intelligence remains within OmniScan.
//
// Parent can call:
//
//     evaVideoRef.current.speak(text)
//
// Optional callbacks:
//
//     onReady()
//     onSpeechStart()
//     onSpeechEnd()
//
//========================================================

const EvaVideo = forwardRef(
    function EvaVideo(
        {
            visible,
			variant = "default",
            onReady,
            onSpeechStart,
            onSpeechEnd
        },
        ref
    ) {

        //----------------------------------------------------
        // References
        //----------------------------------------------------

        const videoRef = useRef(null);
        const audioRef = useRef(null);

        const simliClientRef = useRef(null);
        const startingRef = useRef(false);

        // Keep latest callbacks without restarting Simli.

        const callbacksRef = useRef({
            onReady,
            onSpeechStart,
            onSpeechEnd
        });

        callbacksRef.current = {
            onReady,
            onSpeechStart,
            onSpeechEnd
        };

        //----------------------------------------------------
        // State
        //----------------------------------------------------

        const [connecting, setConnecting] =
            useState(false);

        const [connected, setConnected] =
            useState(false);

        const [error, setError] =
            useState(null);

        //====================================================
        // Speak
        //====================================================

        const speak = async (text) => {

            //------------------------------------------------
            // Validate Text
            //------------------------------------------------

            if (!text || !text.trim()) {
                return;
            }

            //------------------------------------------------
            // Get Simli Client
            //------------------------------------------------

            const simliClient =
                simliClientRef.current;

            if (!simliClient) {

                console.warn(
                    "Eva cannot speak because Simli is not connected."
                );

                return;
            }

            try {

                //------------------------------------------------
                // Generate Speech
                //------------------------------------------------

                console.log(
                    "GENERATING EVA SPEECH:",
                    text
                );

                const audioBytes =
                    await generateSpeech(text);

                console.log(
                    "EVA SPEECH RECEIVED:",
                    audioBytes.length,
                    "bytes"
                );

                //------------------------------------------------
                // Ensure Client Still Exists
                //------------------------------------------------

                if (
                    simliClientRef.current !==
                    simliClient
                ) {

                    console.warn(
                        "Eva Simli session changed before speech could be played."
                    );

                    return;
                }

                //------------------------------------------------
                // Send PCM Audio To Simli
                //------------------------------------------------

                simliClient.sendAudioData(
                    audioBytes
                );

                console.log(
                    "EVA SPEECH SENT TO SIMLI"
                );

            }
            catch (exception) {

                console.error(
                    "Unable to play Eva speech:",
                    exception
                );

                throw exception;
            }

        };

        //====================================================
        // Expose Eva Controls To Parent
        //====================================================

        useImperativeHandle(
            ref,
            () => ({
                speak
            })
        );

        //====================================================
        // Start Conversation
        //====================================================

        const startConversation = async () => {

            //------------------------------------------------
            // Prevent Duplicate Sessions
            //------------------------------------------------

            if (
                startingRef.current ||
                simliClientRef.current
            ) {
                return;
            }

            startingRef.current = true;

            try {

                //------------------------------------------------
                // Reset State
                //------------------------------------------------

                setConnecting(true);
                setConnected(false);
                setError(null);

                //------------------------------------------------
                // Create Simli Client
                //------------------------------------------------

                const simliClient =
                    await createSimliClient(
                        videoRef.current,
                        audioRef.current
                    );

                //------------------------------------------------
                // Store Client
                //------------------------------------------------

                simliClientRef.current =
                    simliClient;

                //------------------------------------------------
                // Simli Started
                //------------------------------------------------

                simliClient.on(
                    "start",
                    () => {

                        console.log(
                            "SIMLI EVA STARTED"
                        );

                        setConnected(true);
                        setConnecting(false);

                        //----------------------------------------
                        // Notify Parent
                        //----------------------------------------

                        callbacksRef.current
                            .onReady?.();

                        //----------------------------------------
                        // Existing Video Diagnostics
                        //----------------------------------------

                        setTimeout(
                            () => {

                                console.log(
                                    "SIMLI NATIVE VIDEO:",
                                    videoRef.current?.videoWidth,
                                    "x",
                                    videoRef.current?.videoHeight
                                );

                                const stream =
                                    videoRef.current?.srcObject;

                                const track =
                                    stream
                                        ?.getVideoTracks()
                                        ?.[0];

                                console.log(
                                    "SIMLI VIDEO TRACK SETTINGS:",
                                    track?.getSettings()
                                );

                            },
                            2000
                        );

                    }
                );

                //------------------------------------------------
                // Simli Stopped
                //------------------------------------------------

                simliClient.on(
                    "stop",
                    () => {

                        console.log(
                            "SIMLI EVA STOPPED"
                        );

                        setConnected(false);
                        setConnecting(false);

                    }
                );

                //------------------------------------------------
                // Simli Speaking
                //------------------------------------------------

                simliClient.on(
                    "speaking",
                    () => {

                        console.log(
                            "SIMLI EVA SPEAKING"
                        );

                        callbacksRef.current
                            .onSpeechStart?.();

                    }
                );

                //------------------------------------------------
                // Simli Silent
                //------------------------------------------------

                simliClient.on(
                    "silent",
                    () => {

                        console.log(
                            "SIMLI EVA SILENT"
                        );

                        callbacksRef.current
                            .onSpeechEnd?.();

                    }
                );

                //------------------------------------------------
                // Simli Error
                //------------------------------------------------

                simliClient.on(
                    "error",
                    event => {

                        console.error(
                            "SIMLI EVA ERROR:",
                            event
                        );

                        setError(
                            "Unable to connect to Eva."
                        );

                        setConnecting(false);

                    }
                );

                //------------------------------------------------
                // Simli Startup Error
                //------------------------------------------------

                simliClient.on(
                    "startup_error",
                    message => {

                        console.error(
                            "SIMLI EVA STARTUP ERROR:",
                            message
                        );

                        setError(
                            message ||
                            "Unable to start Eva."
                        );

                        setConnecting(false);
                        setConnected(false);

                    }
                );

                //------------------------------------------------
                // Start Simli
                //------------------------------------------------

                await simliClient.start();

            }
            catch (exception) {

                //------------------------------------------------
                // Log Error
                //------------------------------------------------

                console.error(
                    "Unable to start Eva Simli session:",
                    exception
                );

                //------------------------------------------------
                // Update State
                //------------------------------------------------

                setError(
                    exception.message
                );

                setConnecting(false);
                setConnected(false);

                //------------------------------------------------
                // Clean Failed Client
                //------------------------------------------------

                const simliClient =
                    simliClientRef.current;

                simliClientRef.current = null;

                if (simliClient) {

                    try {

                        await stopSimliClient(
                            simliClient
                        );

                    }
                    catch (stopException) {

                        console.warn(
                            "Unable to stop failed Simli session:",
                            stopException
                        );

                    }

                }

            }
            finally {

                //------------------------------------------------
                // Allow Future Startup
                //------------------------------------------------

                startingRef.current = false;

            }

        };

        //====================================================
        // Destroy Conversation
        //====================================================

        const destroyConversation = async () => {

            //------------------------------------------------
            // Get Existing Client
            //------------------------------------------------

            const simliClient =
                simliClientRef.current;

            //------------------------------------------------
            // Clear References Immediately
            //------------------------------------------------

            simliClientRef.current = null;
            startingRef.current = false;

            //------------------------------------------------
            // Stop Simli
            //------------------------------------------------

            if (simliClient) {

                try {

                    await stopSimliClient(
                        simliClient
                    );

                    console.log(
                        "SIMLI EVA DESTROYED"
                    );

                }
                catch (exception) {

                    console.warn(
                        "Unable to stop Eva Simli session:",
                        exception
                    );

                }

            }

            //------------------------------------------------
            // Reset State
            //------------------------------------------------

            setConnected(false);
            setConnecting(false);

        };

        //====================================================
        // Open / Close Eva
        //====================================================

        useEffect(
            () => {

                //------------------------------------------------
                // Eva Closed
                //------------------------------------------------

                if (!visible) {

                    destroyConversation();

                    return;
                }

                //------------------------------------------------
                // Eva Opened
                //------------------------------------------------

                startConversation();

                //------------------------------------------------
                // Component Cleanup
                //------------------------------------------------

                return () => {

                    destroyConversation();

                };

            },
            [visible]
        );

        //====================================================
        // Render
        //====================================================

        return (

			<div
			    className={
			        "eva-live-expert-video-container " +
			        (
			            visible
			                ? "eva-live-expert-video-container-visible "
			                : ""
			        ) +
			        (
			            variant === "briefing"
			                ? "eva-live-expert-video-container-briefing"
			                : ""
			        )
			    }
			>

                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{
                        position: "absolute",
                        inset: "0",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        background: "#000"
                    }}
                />

                <audio
                    ref={audioRef}
                    autoPlay
                />

                {connecting && !connected && (

                    <div
                        style={{
                            position: "absolute",
                            left: "20px",
                            bottom: "20px",
                            padding: "10px 16px",
                            zIndex: 10,
                            pointerEvents: "none"
                        }}
                    >
                        Connecting to Eva...
                    </div>

                )}

                {error && (

                    <div
                        style={{
                            position: "absolute",
                            left: "20px",
                            right: "20px",
                            bottom: "20px",
                            padding: "12px",
                            zIndex: 10
                        }}
                    >
                        {error}
                    </div>

                )}

            </div>

        );

    }
);

export default EvaVideo;
